import { AUDIO_STALL_TIMEOUT } from '../constants/defaults';
import { SKIP_AUDIO_THRESHOLD_SECONDS } from '../constants/skipAudioConfig';

export class AudioEngine {
  private currentAudio: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private _isPlaying: boolean = false;
  private currentNumber: number = 0;
  private playbackStartTime: number = 0;
  private progressTimerId: ReturnType<typeof setInterval> | null = null;
  private stallTimerId: ReturnType<typeof setTimeout> | null = null;
  private _volume: number = 1.0;

  onComplete: ((number: number, skipped: boolean) => void) | null = null;
  onError: ((number: number, error: Error) => void) | null = null;
  onLongAudio: ((number: number, elapsedSeconds: number) => void) | null = null;
  onProgress: ((number: number, elapsedSeconds: number, totalSeconds: number | null) => void) | null = null;

  initialize(): void {
    try {
      const AudioCtx = window.AudioContext || (window as unknown as Record<string, unknown>).webkitAudioContext as typeof AudioContext;
      if (AudioCtx) {
        this.audioContext = new AudioCtx();
      }
    } catch (e) {
      console.warn('[AudioEngine] AudioContext initialization failed:', e);
    }
  }

  async play(audioUrl: string, number: number): Promise<void> {
    if (this._isPlaying) {
      throw new Error('Audio already playing');
    }

    this.currentNumber = number;
    this._isPlaying = true;
    this.playbackStartTime = Date.now();

    // Resume AudioContext if suspended
    if (this.audioContext?.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch {
        // Ignore
      }
    }

    const audio = new Audio(audioUrl);
    audio.volume = this._volume;
    audio.preload = 'auto';
    this.currentAudio = audio;

    return new Promise<void>((resolve) => {
      const cleanup = () => {
        this.clearProgressTimer();
        this.clearStallTimer();
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };

      const onEnded = () => {
        cleanup();
        this._isPlaying = false;
        if (this.onComplete) {
          this.onComplete(number, false);
        }
        resolve();
      };

      const onError = () => {
        cleanup();
        this._isPlaying = false;
        const error = new Error(`Audio load failed: ${audioUrl}`);
        if (this.onError) {
          this.onError(number, error);
        }
        resolve(); // Resolve to not break the chain
      };

      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      // Start progress tracking
      this.startProgressTimer(number);

      // Start stall detection
      this.startStallDetection(audio, number, audioUrl, cleanup, resolve);

      audio.play().catch((err) => {
        cleanup();
        this._isPlaying = false;
        if (this.onError) {
          this.onError(number, err instanceof Error ? err : new Error(String(err)));
        }
        resolve();
      });
    });
  }

  forceSkip(): void {
    if (this.currentAudio && this._isPlaying) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.clearProgressTimer();
      this.clearStallTimer();
      this._isPlaying = false;
      if (this.onComplete) {
        this.onComplete(this.currentNumber, true);
      }
    }
  }

  isPlaying(): boolean {
    return this._isPlaying;
  }

  getPlaybackElapsed(): number {
    if (!this._isPlaying) return 0;
    return (Date.now() - this.playbackStartTime) / 1000;
  }

  getDuration(): number | null {
    if (!this.currentAudio) return null;
    const d = this.currentAudio.duration;
    return isNaN(d) || !isFinite(d) ? null : d;
  }

  setVolume(level: number): void {
    this._volume = Math.max(0, Math.min(1, level));
    if (this.currentAudio) {
      this.currentAudio.volume = this._volume;
    }
  }

  destroy(): void {
    this.forceSkip();
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }

  private startProgressTimer(number: number): void {
    this.clearProgressTimer();
    this.progressTimerId = setInterval(() => {
      if (!this._isPlaying) {
        this.clearProgressTimer();
        return;
      }
      const elapsed = this.getPlaybackElapsed();
      const total = this.getDuration();

      if (this.onProgress) {
        this.onProgress(number, elapsed, total);
      }

      // Check for long audio threshold
      if (elapsed >= SKIP_AUDIO_THRESHOLD_SECONDS && this.onLongAudio) {
        this.onLongAudio(number, elapsed);
      }
    }, 1000);
  }

  private startStallDetection(
    audio: HTMLAudioElement,
    number: number,
    audioUrl: string,
    cleanup: () => void,
    resolve: () => void
  ): void {
    this.clearStallTimer();
    let hasProgress = false;

    const onProgress = () => {
      hasProgress = true;
    };
    audio.addEventListener('progress', onProgress);
    audio.addEventListener('timeupdate', onProgress);

    this.stallTimerId = setTimeout(() => {
      audio.removeEventListener('progress', onProgress);
      audio.removeEventListener('timeupdate', onProgress);

      if (!hasProgress && this._isPlaying) {
        cleanup();
        this._isPlaying = false;
        audio.pause();
        if (this.onError) {
          this.onError(number, new Error(`Network stall: ${audioUrl}`));
        }
        resolve();
      }
    }, AUDIO_STALL_TIMEOUT);
  }

  private clearProgressTimer(): void {
    if (this.progressTimerId !== null) {
      clearInterval(this.progressTimerId);
      this.progressTimerId = null;
    }
  }

  private clearStallTimer(): void {
    if (this.stallTimerId !== null) {
      clearTimeout(this.stallTimerId);
      this.stallTimerId = null;
    }
  }
}
